import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowRight,
  ExternalLink,
  Newspaper,
  RefreshCw,
  Star,
  StarOff,
  EyeOff,
  Eye,
  Filter,
  Clock,
} from 'lucide-react';
import { LogoFull, LogoMark } from '../components/Logo';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, formatDateTime } from '../lib/constants';

export default function News() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], sources: [], last_refresh: null, total: 0 });
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('todas');
  const [showHidden, setShowHidden] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const isAdmin = user?.role === 'admin';

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (source !== 'todas') params.set('source', source);
      if (showHidden && isAdmin) params.set('include_hidden', 'true');
      const { data } = await api.get(`/news?${params.toString()}`);
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [source, showHidden]); // eslint-disable-line react-hooks/exhaustive-deps

  const featured = useMemo(() => data.items.filter((i) => i.featured && !i.hidden).slice(0, 3), [data.items]);
  const regular = useMemo(
    () => data.items.filter((i) => (!i.featured || (showHidden && isAdmin)) && (showHidden ? true : !i.hidden)),
    [data.items, showHidden, isAdmin],
  );

  const refreshNow = async () => {
    if (!isAdmin) return;
    setRefreshing(true);
    try {
      const { data } = await api.post('/news/refresh');
      toast.success(`Actualizado · ${data.inserted} nuevas, ${data.updated} refrescadas`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No pudimos actualizar');
    } finally {
      setRefreshing(false);
    }
  };

  const toggleFeature = async (item) => {
    try {
      const { data } = await api.post(`/news/${item.id}/feature`);
      setData((d) => ({ ...d, items: d.items.map((x) => (x.id === item.id ? { ...x, featured: data.featured } : x)) }));
      toast.success(data.featured ? 'Destacada' : 'Quitada de destacadas');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No pudimos actualizar');
    }
  };

  const toggleHide = async (item) => {
    try {
      const { data } = await api.post(`/news/${item.id}/hide`);
      setData((d) => ({ ...d, items: d.items.map((x) => (x.id === item.id ? { ...x, hidden: data.hidden } : x)) }));
      toast.success(data.hidden ? 'Oculta del sitio público' : 'Vuelve a ser visible');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No pudimos actualizar');
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="news-page">
      {/* NAV público (idéntico al de landing) */}
      <header className="border-b border-[#E5E7EB] bg-white/95 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="news-nav-home">
            <LogoFull height={28} />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-[13.5px] text-[#374151]">
            <Link to="/" className="hover:text-[#1C2329]">Inicio</Link>
            <Link to="/noticias" className="text-[#143A34] font-medium">Noticias</Link>
            <a href="/#modulos" className="hover:text-[#1C2329]">Módulos</a>
            <a href="/#ley" className="hover:text-[#1C2329]">Ley 21.719</a>
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <Link to="/app/dashboard" className="pv-btn-primary flex items-center gap-2" data-testid="news-goto-app">
                Ir al panel <ArrowRight size={14} />
              </Link>
            ) : (
              <>
                <Link to="/login" className="text-[13.5px] text-[#374151] hover:text-[#1C2329] px-3 py-2">Ingresar</Link>
                <Link to="/registro" className="pv-btn-primary flex items-center gap-2">
                  Crear organización <ArrowRight size={14} />
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="border-b border-[#E5E7EB] bg-[#FAFBFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
          <div className="flex items-start gap-5">
            <div className="w-11 h-11 rounded-md bg-white border border-[#E5E7EB] flex items-center justify-center text-[#2A9B68] shrink-0 mt-2">
              <Newspaper size={20} strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <h1 className="text-[32px] md:text-[44px] font-semibold tracking-[-0.03em] text-[#1C2329] leading-[1.05]">
                Noticias de protección de datos en Chile.
              </h1>
              <p className="mt-5 text-[15px] text-[#374151] max-w-2xl leading-relaxed">
                Seleccionamos automáticamente cobertura de medios chilenos sobre la Ley N.º 21.719, brechas de seguridad, derechos ARCO+ y la nueva Agencia de Protección de Datos. Cada nota lleva al artículo original del medio.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-4 text-[12.5px] text-[#6B7280] font-mono">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2A9B68]" />
                  {data.total} notas seleccionadas
                </span>
                {data.last_refresh?.at && (
                  <span className="inline-flex items-center gap-1.5">
                    <Clock size={12} /> {formatDateTime(data.last_refresh.at)}
                  </span>
                )}
              </div>
            </div>
            {isAdmin && (
              <button
                onClick={refreshNow}
                disabled={refreshing}
                className="pv-btn-secondary hidden md:flex items-center gap-2 text-[13px] shrink-0"
                data-testid="news-refresh"
              >
                <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
                {refreshing ? 'Actualizando…' : 'Actualizar ahora'}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* FILTROS */}
      <section className="border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-[12px] text-[#6B7280] uppercase tracking-wider">
            <Filter size={14} /> Fuente
          </div>
          <SourceButton active={source === 'todas'} onClick={() => setSource('todas')}>Todas</SourceButton>
          {data.sources.map((s) => (
            <SourceButton key={s} active={source === s} onClick={() => setSource(s)}>{s}</SourceButton>
          ))}
          {isAdmin && (
            <label className="ml-auto flex items-center gap-2 text-[12.5px] text-[#374151] cursor-pointer">
              <input
                type="checkbox"
                checked={showHidden}
                onChange={(e) => setShowHidden(e.target.checked)}
                className="accent-[#2A9B68]"
                data-testid="news-show-hidden"
              />
              Mostrar ocultas (admin)
            </label>
          )}
        </div>
      </section>

      {/* CONTENIDO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        {loading ? (
          <div className="text-sm text-[#6B7280]">Cargando noticias…</div>
        ) : data.items.length === 0 ? (
          <EmptyState isAdmin={isAdmin} onRefresh={refreshNow} refreshing={refreshing} />
        ) : (
          <>
            {featured.length > 0 && (
              <div className="mb-12">
                <div className="pv-eyebrow mb-4 text-[#2A9B68] flex items-center gap-2">
                  <Star size={12} /> Destacadas por el equipo Priviax
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {featured.map((n) => (
                    <FeatureCard key={n.id} item={n} isAdmin={isAdmin} onFeature={toggleFeature} onHide={toggleHide} />
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {regular.map((n) => (
                <NewsCard key={n.id} item={n} isAdmin={isAdmin} onFeature={toggleFeature} onHide={toggleHide} />
              ))}
            </div>
          </>
        )}
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#E5E7EB] bg-[#FAFBFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[12.5px] text-[#6B7280]">
          <div className="flex items-center gap-3">
            <LogoMark size={20} />
            <span>© {new Date().getFullYear()} Priviax · Los titulares y extractos pertenecen a sus medios originales.</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/" className="hover:text-[#1C2329]">Inicio</Link>
            <Link to="/login" className="hover:text-[#1C2329]">Ingresar</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function SourceButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`text-[12.5px] px-2.5 py-1.5 rounded-md border transition-colors ${active ? 'bg-[#0B1220] text-white border-[#0B1220]' : 'bg-white text-[#374151] border-[#E5E7EB] hover:bg-[#FAFBFC]'}`}
      data-testid={`news-source-${children}`}
    >
      {children}
    </button>
  );
}

function FeatureCard({ item, isAdmin, onFeature, onHide }) {
  return (
    <article className="pv-card p-6 flex flex-col h-full" data-testid={`news-featured-${item.id}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#ECFDF5] text-[#065F46] border border-[#2A9B68]/30 uppercase tracking-wider">
          Destacada
        </span>
        <span className="text-[11px] text-[#6B7280]">{formatDate(item.published_at)}</span>
      </div>
      <h3 className="mt-4 text-[17px] font-semibold text-[#1C2329] leading-snug tracking-tight line-clamp-3">
        {item.title}
      </h3>
      <p className="mt-2 text-[13.5px] text-[#6B7280] leading-relaxed line-clamp-3">{item.summary}</p>
      <div className="mt-4 flex items-center justify-between text-[12px]">
        <span className="text-[#143A34] font-medium">{item.source}</span>
        <a href={item.url} target="_blank" rel="noreferrer" className="text-[#2A9B68] hover:text-[#228256] inline-flex items-center gap-1" data-testid={`news-open-${item.id}`}>
          Leer en el medio <ExternalLink size={12} />
        </a>
      </div>
      {isAdmin && <AdminBar item={item} onFeature={onFeature} onHide={onHide} />}
    </article>
  );
}

function NewsCard({ item, isAdmin, onFeature, onHide }) {
  return (
    <article
      className={`pv-card p-5 flex flex-col ${item.hidden ? 'opacity-60' : ''}`}
      data-testid={`news-card-${item.id}`}
    >
      <div className="flex items-center justify-between text-[12px]">
        <span className="text-[#143A34] font-medium">{item.source}</span>
        <span className="text-[#6B7280]">{formatDate(item.published_at)}</span>
      </div>
      <a
        href={item.url}
        target="_blank"
        rel="noreferrer"
        className="mt-3 text-[15.5px] font-medium text-[#1C2329] leading-snug hover:text-[#143A34] line-clamp-3"
        data-testid={`news-title-${item.id}`}
      >
        {item.title}
      </a>
      {item.summary && (
        <p className="mt-2 text-[13px] text-[#6B7280] leading-relaxed line-clamp-2">{item.summary}</p>
      )}
      {item.keywords_matched?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {item.keywords_matched.slice(0, 3).map((kw) => (
            <span key={kw} className="text-[10.5px] px-1.5 py-0.5 rounded bg-[#F3F4F6] text-[#374151] border border-[#E5E7EB]">
              {kw}
            </span>
          ))}
        </div>
      )}
      <div className="mt-4 pt-3 border-t border-[#E5E7EB] flex items-center justify-between">
        <a
          href={item.url}
          target="_blank"
          rel="noreferrer"
          className="text-[12.5px] text-[#2A9B68] hover:text-[#228256] inline-flex items-center gap-1"
          data-testid={`news-open-${item.id}`}
        >
          Leer nota <ExternalLink size={12} />
        </a>
        {isAdmin && <AdminBar item={item} onFeature={onFeature} onHide={onHide} inline />}
      </div>
    </article>
  );
}

function AdminBar({ item, onFeature, onHide, inline }) {
  return (
    <div className={`${inline ? '' : 'mt-4 pt-4 border-t border-[#E5E7EB]'} flex items-center gap-2 text-[12px]`}>
      <button
        onClick={() => onFeature(item)}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded border ${item.featured ? 'bg-[#ECFDF5] text-[#065F46] border-[#2A9B68]/30' : 'border-[#E5E7EB] text-[#374151] hover:bg-[#FAFBFC]'}`}
        data-testid={`news-feature-${item.id}`}
        title={item.featured ? 'Quitar destacada' : 'Destacar'}
      >
        {item.featured ? <Star size={12} /> : <StarOff size={12} />}
        {item.featured ? 'Destacada' : 'Destacar'}
      </button>
      <button
        onClick={() => onHide(item)}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded border ${item.hidden ? 'bg-[#FEF2F2] text-[#991B1B] border-[#FCA5A5]/60' : 'border-[#E5E7EB] text-[#374151] hover:bg-[#FAFBFC]'}`}
        data-testid={`news-hide-${item.id}`}
        title={item.hidden ? 'Volver a mostrar' : 'Ocultar del sitio'}
      >
        {item.hidden ? <Eye size={12} /> : <EyeOff size={12} />}
        {item.hidden ? 'Oculta' : 'Ocultar'}
      </button>
    </div>
  );
}

function EmptyState({ isAdmin, onRefresh, refreshing }) {
  return (
    <div className="pv-card p-12 text-center max-w-2xl mx-auto" data-testid="news-empty">
      <Newspaper size={26} className="mx-auto text-[#9CA3AF]" strokeWidth={1.5} />
      <div className="mt-4 text-lg font-semibold text-[#1C2329]">Todavía no hay noticias cargadas</div>
      <div className="mt-1 text-sm text-[#6B7280]">
        Consultamos medios chilenos cada 30 minutos. Si eres administrador, puedes ejecutar una carga manual.
      </div>
      {isAdmin && (
        <button onClick={onRefresh} disabled={refreshing} className="mt-6 pv-btn-primary inline-flex items-center gap-2">
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Actualizando…' : 'Actualizar ahora'}
        </button>
      )}
    </div>
  );
}
